import { useMemo, useRef } from 'react';
import { useField, useForm, SubmitResult } from '@shopify/react-form';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import { RestrictionType } from '@sdk/messages/types/RestrictionType';
import { useSession } from '@sdk/session/SessionContext';
import isOrgFeatureEnabled from '@sdk/authentication/helpers/isOrgFeatureEnabled';
import { Feature } from '@sdk/common/features';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';

import { FormSubmitStatuses } from '@web/forms/types';
import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import { Conversation } from '@web/conversations/types/Conversation';
import { useRestrictedToUsersField } from '@web/conversations/ComposeConversation/composeConversation.hooks';
import { useNavigate } from '@web/Router/router.hooks';
import { useUpdateConversation } from '@web/conversations/hooks/useUpdateConversation';

import { getConversationEditPayload, getPickerPersonas } from './conversationEdit.utils';

type Props = { conversation: Conversation };

export const useConversationEdit = ({ conversation }: Props) => {
  const submitPromise = useRef<Promise<SubmitResult>>();
  const { showErrorToast } = useToast();
  const navigate = useNavigate();
  const { mutateAsync } = useUpdateConversation();

  const formFields = useConversationEditFormFields(conversation);
  const {
    dirty: isDirty,
    fields,
    reset,
    validate,
    submit: formSubmit,
    submitErrors,
  } = useForm({
    fields: formFields,
    onSubmit: async (fieldValues) => {
      try {
        const editPayload = getConversationEditPayload(conversation.id, fieldValues);
        await mutateAsync(editPayload);

        navigate.toConversationDetails({
          params: { id: conversation.id, slug: conversation.slug },
        });

        submitPromise.current = Promise.resolve({
          status: FormSubmitStatuses.success,
        });
        return submitPromise.current;
      } catch (e) {
        const errorMsg = getErrorForToast(e);
        showErrorToast(errorMsg);

        submitPromise.current = Promise.resolve({
          status: FormSubmitStatuses.fail,
          errors: [{ message: errorMsg }],
        });
        return submitPromise.current;
      }
    },
  });

  const isFormValid = useMemo(() => {
    const result = validate();

    return !result.length;
    // @TODO rewrite using react-hook-form, shopify lib does not support onChange validation,
    // hence such workaround has been used
    // eslint-disable-next-line
  }, [fields.members.value, fields.restricted.value, fields.restrictedToUsers.value, validate]);

  const submit = decorateSubmit(formSubmit, submitPromise);

  const cancel = () => {
    reset();
    navigate.toConversationDetails({
      params: { id: conversation.id, slug: conversation.slug },
    });
  };

  return { fields, isDirty, isFormValid, submit, cancel, submitErrors };
};

const useConversationEditFormFields = (conversation: Conversation) => {
  const { personasDict } = usePersonaCollection();
  const { currentUser } = useSession();
  const { members, restrictedToUsers } = useMemo(
    () => ({
      members: getPickerPersonas(conversation.memberIds, personasDict),
      restrictedToUsers: getPickerPersonas(conversation.restrictedToPersonas, personasDict),
    }),
    [conversation, personasDict]
  );

  const restricted = useField({
    value: isOrgFeatureEnabled(Feature.conversationRestrictions, currentUser)
      ? conversation.restricted
      : RestrictionType.NONE,
    validates: [],
  });

  const restrictedToUsersFields = useRestrictedToUsersField(restricted.value, restrictedToUsers);

  const name = useField({
    value: conversation.name,
    validates: [],
  });

  const membersField = useField({
    value: members,
    validates: [],
  });

  return {
    members: membersField,
    name,
    restricted,
    restrictedToUsers: restrictedToUsersFields,
  };
};
